import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, of, throwError } from 'rxjs';
import { environment } from '../environment/environment';
import type {
  PlanDto,
  SessionUrlDto,
  SubscriptionDto,
} from '../models';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private readonly http = inject(HttpClient);
  private readonly api = environment.urls.backend.api;

  /** List purchasable Stripe plans — GET /billing/stripe/plans */
  getPlans(): Observable<PlanDto[]> {
    return this.http.get<PlanDto[]>(`${this.api}/billing/stripe/plans`);
  }

  /** Current user's subscription (provider-agnostic) — GET /billing/subscription.
   * Returns null when the user has no billing row (404). */
  getSubscription(): Observable<SubscriptionDto | null> {
    return this.http.get<SubscriptionDto>(`${this.api}/billing/subscription`).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) return of(null);
        return throwError(() => err);
      }),
    );
  }

  /** Create Stripe checkout session — POST /billing/stripe/checkout-session */
  createCheckoutSession(priceId?: string, successUrl?: string, cancelUrl?: string): Observable<SessionUrlDto> {
    const body: Record<string, string> = {};
    if (priceId) body['priceId'] = priceId;
    if (successUrl) body['successUrl'] = successUrl;
    if (cancelUrl) body['cancelUrl'] = cancelUrl;
    return this.http.post<SessionUrlDto>(`${this.api}/billing/stripe/checkout-session`, body);
  }

  /** Create Stripe customer portal session — POST /billing/stripe/portal-session */
  createPortalSession(returnUrl?: string): Observable<SessionUrlDto> {
    const body = returnUrl ? { returnUrl } : {};
    return this.http.post<SessionUrlDto>(`${this.api}/billing/stripe/portal-session`, body);
  }

  /** Cancel Stripe subscription at period end — POST /billing/stripe/cancel */
  cancelSubscription(): Observable<SubscriptionDto> {
    return this.http.post<SubscriptionDto>(`${this.api}/billing/stripe/cancel`, {});
  }

  /** Reactivate a Stripe subscription scheduled for cancellation — POST /billing/stripe/reactivate */
  reactivateSubscription(): Observable<SubscriptionDto> {
    return this.http.post<SubscriptionDto>(`${this.api}/billing/stripe/reactivate`, {});
  }

  /** Request a Stripe refund (within the configured window) — POST /billing/stripe/refund */
  requestRefund(): Observable<SubscriptionDto> {
    return this.http.post<SubscriptionDto>(`${this.api}/billing/stripe/refund`, {});
  }

  /** Register an App Store purchase with the backend for Apple-side validation.
   * Returns the canonical SubscriptionDto on success so the caller can update
   * local state without a follow-up getSubscription() call.
   * POST /billing/apple/verify */
  notifyIapPurchase(transactionId: string): Observable<SubscriptionDto> {
    return this.http.post<SubscriptionDto>(`${this.api}/billing/apple/verify`, { transactionId });
  }

  /** Re-sync subscription from Apple's latest verified transaction (restore flow).
   * 404 → null (no prior Apple purchase, benign).
   * 409 / 503 → propagated so the caller can show specific messages.
   * POST /billing/apple/restore */
  restoreIapPurchase(): Observable<SubscriptionDto | null> {
    return this.http.post<SubscriptionDto>(`${this.api}/billing/apple/restore`, {}).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) return of(null);
        return throwError(() => err);
      }),
    );
  }
}
